import {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  ReactNode,
  Children,
  isValidElement,
  cloneElement,
  CSSProperties,
} from 'react';
import { DropdownChevron } from 'components/common/svgIcons';
import { ValueCallback } from 'types/callbacks';
import styles from './DropDown.module.css';

type DropDownOptionProps = {
  onSelect?: ValueCallback<string | number>;
  textAlign?: 'left' | 'center' | 'right';
  includeDivider?: boolean;
  value: string | number;
  selected?: boolean;
  children: ReactNode;
};

export const DropDownOption: FunctionComponent<DropDownOptionProps> = ({
  onSelect,
  textAlign = 'center',
  includeDivider = false,
  value,
  selected = false,
  children,
}) => {
  function onClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (onSelect !== undefined && !selected) {
      onSelect(value);
    }
  }

  return (
    <div>
      {includeDivider && <div className={styles.Divider} />}
      <button
        onClick={onClick}
        style={{ color: selected ? '#f1f1f1' : '#888888', textAlign }}
      >
        {children}
      </button>
    </div>
  );
};

interface DropDownProps {
  optionKey: string;
  displayValue: string;
  displayValueStyle?: CSSProperties;
  optionListStyle?: CSSProperties;
  disabled?: boolean;
  onSelect: ValueCallback<string | number>;
  children: ReactNode;
}
interface DropDownDisabledProps {
  optionKey: string;
  displayValue: string;
  displayValueStyle?: CSSProperties;
  optionListStyle?: CSSProperties;
  disabled?: boolean;
  children?: never[];
  onSelect?: never;
}

const DropDown: FunctionComponent<DropDownProps | DropDownDisabledProps> = ({
  optionKey,
  onSelect,
  disabled = false,
  displayValue,
  children,
  displayValueStyle,
  optionListStyle,
}) => {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    isOpen
      ? window.addEventListener('click', close, true)
      : window.removeEventListener('click', close, true);

    return () => {
      window.removeEventListener('click', close, true);
    };
  }, [isOpen]);

  useEffect(() => {
    setLoading(false);
  }, [optionKey]);

  const close = (event: MouseEvent) => {
    if (isOpen && !ref.current?.contains(event.target)) {
      setOpen(false);
    }
  };

  const loading = () => {
    return <div className={styles.Loader} />;
  };

  function onOptionSelect(value: string) {
    setOpen(false);
    onSelect?.(value);
    setLoading(true);
  }

  const optionsWithOnSelect = Children.map<ReactNode, ReactNode>(
    children,
    (child) => {
      if (isValidElement(child)) {
        return cloneElement(child, { onSelect: onOptionSelect } as any);
      }
      return child;
    }
  );

  const isDisabled =
    disabled || onSelect === undefined || children === undefined;

  return (
    <div className={styles.Container}>
      {isLoading ? (
        loading()
      ) : (
        <div
          className={styles.DropDownHeader}
          style={{ cursor: isDisabled ? 'auto' : 'pointer' }}
          onClick={() => !isDisabled && setOpen(true)}
        >
          <h4 style={displayValueStyle}>{displayValue}</h4>
          <DropdownChevron hide={isDisabled} />
        </div>
      )}
      {isOpen && (
        <div ref={ref} className={styles.OptionList} style={optionListStyle}>
          {optionsWithOnSelect}
        </div>
      )}
    </div>
  );
};

export default DropDown;
